/**
 * How fast this event's rounds run.
 *
 * The server can answer a round two ways: synchronously, holding the request
 * open for the whole pipeline, or queued, answering with a row the page polls.
 * The queued path is the one that survives a slow round — the request never has
 * to stay inside the platform's time limit — but it is also one extra round trip
 * and a page that shows a progress counter, so the interface only asks for it
 * when it has a reason to.
 *
 * The reason is the event's own history: a round that already took longer than
 * the threshold is the evidence that the next one will too. The threshold is
 * well inside the platform's request limit, so the first round of an event is
 * still answered inline in the ordinary case, and only an event whose rounds
 * run long switches over.
 */

import type { RoundSummaryDto } from '@shared/types'

import { useEventRounds } from './use-events'
import { useSharedRounds } from './use-shared'

/**
 * A round longer than this makes the queued path the better answer.
 *
 * Fifteen seconds is the SLO the pipeline aims at for a warm round. A round
 * past it is already a round the member would rather read about later than wait
 * for, and the measurement comes from the engine's own record rather than from a
 * guess about the event.
 */
export const SLOW_ROUND_SECONDS = 15

export interface RoundPace {
  /** Whether the next round should be queued rather than waited for. */
  isSlow: boolean
  /** Whether any round of this event has actually finished, so the pace is known. */
  hasEvidence: boolean
}

/** Duration of a finished round, or null while it is still running. */
function durationSeconds(round: RoundSummaryDto): number | null {
  if (round.finishedAt === null) {
    return null
  }
  return Math.max(0, round.finishedAt - round.createdAt)
}

/** Whether any finished round of this event took longer than the threshold. */
export function isSlowPace(rounds: readonly RoundSummaryDto[]): RoundPace {
  let hasEvidence = false
  for (const round of rounds) {
    const duration = durationSeconds(round)
    if (duration === null) {
      continue
    }
    hasEvidence = true
    if (duration > SLOW_ROUND_SECONDS) {
      return { isSlow: true, hasEvidence: true }
    }
  }
  return { isSlow: false, hasEvidence }
}

/**
 * The pace of the signed-in event page.
 *
 * The query is the same one the round history reads, so this costs no extra
 * request: the page already has the rounds when it needs to decide.
 */
export function useEventRoundPace(eventId: string | undefined): RoundPace {
  const rounds = useEventRounds(eventId)
  return isSlowPace(rounds.data?.rounds ?? [])
}

/** The pace of the share link, which reads the same data through its own hook. */
export function useSharedRoundPace(shareToken: string | undefined): RoundPace {
  const rounds = useSharedRounds(shareToken)
  return isSlowPace(rounds.data?.rounds ?? [])
}
